import { CalendarDatePickerPreset, IFiltersValues, TFilterValueRecord } from '@diana/core';
import { TProfileFields, ProfileSignalRQuery, ProfileFieldsArray } from '../types/api/profile';
import { calculateTicks } from './dateUtils';
import { FilterType } from '../stores/useFilters';
import { ProfileParams } from '../apis/Profile';
import { cloneDeep } from 'lodash';

export type TExtendedFilterValueRecord = TFilterValueRecord & {
  preset?: CalendarDatePickerPreset; // Add the 'preset' field
};

export interface IExtendedFiltersValues {
  [key: string]: TExtendedFilterValueRecord[];
}

export const DataRangePresets = [
  CalendarDatePickerPreset.LastThirtyMins,
  CalendarDatePickerPreset.LastTwoHours,
  CalendarDatePickerPreset.LastSixHours,
  CalendarDatePickerPreset.LastTwelveHours,
];

export function convertFiltersToQuery<T extends ProfileSignalRQuery>(
  filters: IExtendedFiltersValues,
): T {
  const query: ProfileSignalRQuery = {};
  if (filters[FilterType.BARGES] && filters[FilterType.BARGES].length > 0) {
    query.SystemIds = filters.barges.map((b) => b.value) as string[];
  }

  if (filters[FilterType.MFM_DEVICES] && filters[FilterType.MFM_DEVICES].length > 0) {
    query.DeviceTickets = {};
    filters[FilterType.MFM_DEVICES].forEach((device) => {
      if (filters[FilterType.BMT_NUMBER] && filters[FilterType.BMT_NUMBER].length > 0) {
        query.DeviceTickets![device.value as string] = filters[FilterType.BMT_NUMBER].map((bmt) =>
          parseInt(bmt.value as string),
        );
      } else {
        query.DeviceTickets![device.value as string] = null;
      }
    });
  }
  // Handle BMT_NUMBER filter even when MFM_DEVICES is not selected
  if (filters[FilterType.BMT_NUMBER] && filters[FilterType.BMT_NUMBER].length > 0) {
    if (!query.DeviceTickets) {
      query.DeviceTickets = {};
    }
    filters[FilterType.BMT_NUMBER].forEach((bmt) => {
      // @ts-ignore
      const deviceId = bmt.deviceId;
      if (!query.DeviceTickets![deviceId]) {
        query.DeviceTickets![deviceId] = [];
      }
      if (bmt.value) {
        query.DeviceTickets![deviceId]!.push(parseInt(bmt.value as string));
      } else {
        query.DeviceTickets![deviceId] = null;
      }
    });
  }
  if (filters[FilterType.TIME_RANGE] && filters[FilterType.TIME_RANGE][0]?.value) {
    // @ts-ignore
    query.TimeRange = {};
    // @ts-ignore
    query.TimeRange.Start = {
      offset: 0,
      date: filters[FilterType.TIME_RANGE][0].value as string,
      ticks: calculateTicks(filters[FilterType.TIME_RANGE][0].value as string),
    };
  }
  const preset = filters[FilterType.TIME_RANGE]?.[0]?.preset;
  // Open-ended presets (rolling windows and the live Current Session) must NOT
  // send an End date — otherwise the live session becomes a fixed window frozen
  // at selection time and totals stop growing.
  const isOpenEndedPreset =
    !!preset &&
    (DataRangePresets.includes(preset as CalendarDatePickerPreset) ||
      preset === CalendarDatePickerPreset.CurrentSession);
  if (!isOpenEndedPreset) {
    if (filters[FilterType.TIME_RANGE]?.[1]?.value) {
      query.TimeRange!.End = {
        offset: 0,
        date: filters[FilterType.TIME_RANGE][1].value as string,
        ticks: calculateTicks(filters[FilterType.TIME_RANGE][1].value as string),
      };
    }
  }

  // query.Fields = fields;

  return query as T;
}

export function convertSingleDeviceFilterToQuery(filters: IExtendedFiltersValues): any {
  const deviceFilter = filters[FilterType.MFM_DEVICES]?.[0]?.value;
  return deviceFilter
    ? {
        DeviceId: deviceFilter as string,
      }
    : null;
}

export function resetBmtNumber(filters: IFiltersValues): any {
  const newData = cloneDeep(filters);
  newData[FilterType.BMT_NUMBER] = [];
  return newData;
}

export function removeQueryIfCurrentSession(filters: IFiltersValues): IFiltersValues | null {
  const newData = cloneDeep(filters);
  const timeRange = newData[FilterType.TIME_RANGE];

  if (
    timeRange &&
    timeRange.length > 0 &&
    timeRange[0].preset === CalendarDatePickerPreset.CurrentSession
  ) {
    return null;
  }
  return newData;
}

export function removeQueryIfOnlyStartDate(filters: IFiltersValues): IFiltersValues | null {
  const newData = cloneDeep(filters);
  if (!newData) {
    return null;
  }
  const timeRange = newData[FilterType.TIME_RANGE];

  if (timeRange && timeRange.length > 0 && !timeRange[0].preset && timeRange[1]?.value === null) {
    return null;
  }
  return newData;
}

export function removeQueryIfNoDevices(filters: IFiltersValues): IFiltersValues | null {
  if (!filters) {
    return null;
  }

  const hasDevices = filters[FilterType.MFM_DEVICES]?.length > 0;
  if (!hasDevices) {
    return null;
  }

  return cloneDeep(filters);
}

export function removeQueryIfNoDeviceSelected(
  filters: IFiltersValues,
  devicesSelected: any,
): IFiltersValues | null {
  const newData = cloneDeep(filters);

  if (!devicesSelected || devicesSelected.length === 0) {
    return null;
  }
  return newData;
}

export function convertFiltersToExportQuery<T extends ProfileParams>(
  filters: IFiltersValues,
  fields: TProfileFields | null = ProfileFieldsArray,
): T {
  const query: any = {};

  // Extract filter  values
  const {
    [FilterType.MFM_DEVICES]: mfmDevices,
    [FilterType.BARGES]: barges,
    [FilterType.TIME_RANGE]: timeRange,
    [FilterType.BMT_NUMBER]: bmtNumbers,
  } = filters || {};

  // MFM Devices
  if (mfmDevices?.length > 0) {
    query.tickets = mfmDevices.map((device) => ({
      deviceId: device.value as string,
    }));
  }

  if (bmtNumbers?.length > 0) {
    query.tickets = bmtNumbers.map((bmt) => ({
      // @ts-ignore
      deviceId: bmt.deviceId,
      ticketNumber: bmt.value,
    }));
  }

  // Barges
  if (barges?.length > 0) {
    query.barges = barges.map((barge) => barge.value as string);
  }

  // From and To dates
  if (timeRange?.length === 2) {
    const [from, to] = timeRange;
    if (from?.value) query.from = from.value;
    if (to?.value) query.to = to.value;
    // @ts-ignore
    if (from?.date) query.from = from.date;
    // @ts-ignore
    if (to?.date) query.to = to.date;
  }

  // Fields
  if (fields) {
    query.fields = fields;
  }

  return query as T;
}

export function updateFiltersForBunkeringDashboard(
  filters: IFiltersValues,
  bunkeringLogs: Array<{
    deviceId: string;
    lastBunkeringPeriod: {
      start: string;
      end: string | null;
    };
  }>,
): IFiltersValues | null {
  const newFilters = cloneDeep(filters)
 
  if (!newFilters) {
    return null;
  }
  if (!newFilters[FilterType.MFM_DEVICES]?.length || !newFilters[FilterType.TIME_RANGE]?.length) {
    return newFilters;
  }

  const selectedDeviceId = newFilters[FilterType.MFM_DEVICES][0].value as string;
  const timeRangePreset = newFilters[FilterType.TIME_RANGE][0].preset;

  const hasMatchingDevice = bunkeringLogs.some((log) => log.deviceId === selectedDeviceId);

  if (!hasMatchingDevice && timeRangePreset === CalendarDatePickerPreset.CurrentSession) {
    newFilters[FilterType.TIME_RANGE][0].preset = CalendarDatePickerPreset.LastSixHours;
  }

  return newFilters;
}
